import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { CheckListAnswer, CheckListAnswerStatus } from 'src/app/model/checklist-field';
import * as _ from 'lodash';
import { Submission } from 'src/app/model/paper';

class CoverableSubmission {

  constructor(answer?: CheckListAnswer, order?: number) {
    this.answer = answer ? answer : new CheckListAnswer();

    if (order) {
      this.answer.order = order;
    }

    this.toBeDeleted = false;
    this.selected = false;
  }

  answer: CheckListAnswer;
  toBeDeleted: boolean;
  selected: boolean;
}

@Component({
  selector: 'app-publication-fixed-control',
  templateUrl: './publication-fixed-control.component.html',
  styleUrls: ['./publication-fixed-control.component.scss']
})
export class PublicationFixedControlComponent implements OnInit, OnChanges {

  @Input() titleLabel: string;
  @Input() formArray: FormArray;
  @Input() checklistAnswers: CheckListAnswer[];

  /**
   * Esta instância está na aba VISÍVEL? As abas inativas continuam montadas (o
   * carrossel do `app-chair-tabs` precisa dos painéis vizinhos no DOM), então
   * sem isto a barra de confirmação de uma aba escondida decolaria por cima da
   * aba visível — o "lar" dela está fora do viewport.
   */
  @Input() active = true;

  submissionsList: CoverableSubmission[];

  /** Há alterações não salvas? Governa a barra de confirmação. */
  dirty = false;

  @Output() updateList: EventEmitter<any> = new EventEmitter;

  @ViewChild(MatTable, {static: false}) table : MatTable<CoverableSubmission>;

  /**
   * Os `app-text-input` da coluna de ID, na ordem das linhas. Usados por
   * `focusOnFirstOccurrence`. Antes aquele método descia oito `children[0]` a
   * partir da linha até achar o `<input>` — mudar o aninhamento de qualquer
   * célula quebrava a legenda em silêncio.
   */
  @ViewChildren('idInput', { read: ElementRef }) idInputs: QueryList<ElementRef<HTMLElement>>;

  columnsShow: string[] = ['position', 'id', 'title', 'event', 'track', 'status', 'delete', 'select'];

  /**
   * Versão do estado (lista + seleção). Tudo que muda um dos dois chama
   * `touch()`; os caches abaixo se invalidam comparando esta versão.
   *
   * Existe porque `statusLength()` e o checkbox master são lidos pelo TEMPLATE,
   * ou seja, a cada ciclo de detecção — e detecção roda em qualquer mousemove
   * sobre a tela. Sem cache: 7 varreduras da lista inteira pelos contadores da
   * legenda, mais 2 pelo checkbox master, continuamente.
   */
  private stateVersion = 0;

  private touch(): void {
    this.stateVersion++;
  }

  /** Linhas que podem entrar numa ação em lote (a linha-fantasma não conta). */
  private get selectableSubmissions(): CoverableSubmission[] {
    return this.submissionsList.filter(s => this.submissionIsSelectable(s));
  }

  // ── Cache dos contadores por status (legenda) ──
  private statusCountsCache: { version: number, counts: { [status: string]: number } } = null;

  private get statusCounts(): { [status: string]: number } {
    if (!this.statusCountsCache || this.statusCountsCache.version !== this.stateVersion) {
      const counts: { [status: string]: number } = {};
      // Uma passada só, em vez de uma por status.
      (this.submissionsList || []).forEach(s => {
        const status = s.answer.status as string;
        counts[status] = (counts[status] || 0) + 1;
      });
      // A linha-fantasma nasce UNCATEGORIZED e não é um item da lista.
      if (counts[CheckListAnswerStatus.UNCATEGORIZED]) {
        counts[CheckListAnswerStatus.UNCATEGORIZED] -= 1;
      }
      this.statusCountsCache = { version: this.stateVersion, counts };
    }
    return this.statusCountsCache.counts;
  }

  // ── Cache do estado da seleção (checkbox master) ──
  private selectionCache: { version: number, selectable: number, selected: number } = null;

  private get selectionState(): { selectable: number, selected: number } {
    if (!this.selectionCache || this.selectionCache.version !== this.stateVersion) {
      let selectable = 0;
      let selected = 0;
      (this.submissionsList || []).forEach(s => {
        if (this.submissionIsSelectable(s)) {
          selectable++;
          if (s.selected) {
            selected++;
          }
        }
      });
      this.selectionCache = { version: this.stateVersion, selectable, selected };
    }
    return this.selectionCache;
  }

  get allRowsSelected(): boolean {
    const { selectable, selected } = this.selectionState;
    return selectable > 0 && selected === selectable;
  }

  get someRowsSelected(): boolean {
    const { selected } = this.selectionState;
    return selected > 0 && !this.allRowsSelected;
  }

  /** Checkbox master do cabeçalho. */
  toggleSelectAll(): void {
    const select = !this.allRowsSelected;
    this.selectableSubmissions.forEach(s => s.selected = select);
    this.touch();
  }

  /** Checkbox de uma linha. */
  toggleSelection(index: number): void {
    const submission = this.submissionsList[index];

    if (!this.submissionIsSelectable(submission)) {
      return;
    }

    submission.selected = !submission.selected;
    this.touch();
    this.saveLastSelectedAnswer(index);
  }

  lastSelectedIndex: number;
  lastSelectedSubmissionId: string;
  deleteOccurred: boolean;

  CheckListAnswerStatus = CheckListAnswerStatus
  CaptionCheckListAnswerStatus = Object.values(CheckListAnswerStatus);

  @HostListener('document:keydown', ['$event'])
  handleDelete(event: KeyboardEvent) {
    if (event.key != 'Delete') {
      return;
    }

    // Só reage se o foco estiver DENTRO desta tabela. Sem isto, um Delete em
    // qualquer ponto da página disparava exclusão nas duas instâncias do
    // componente (Registro de autor e Transferência) ao mesmo tempo.
    if (!this.hostRef.nativeElement.contains(document.activeElement)) {
      return;
    }

    if (!this.deleteOccurred) {
      this.uncoverAllSelectedSubmissions();
    }

    setTimeout(() => this.deleteOccurred = false, 50);
    
    if (this.lastSelectedIndex != undefined) {
      setTimeout(() => {
        this.formArray.controls[this.lastSelectedIndex].get('submissionId').setValue(this.lastSelectedSubmissionId);
        this.submissionsList[this.lastSelectedIndex].answer.submissionId = this.lastSelectedSubmissionId;
      });
    }

  }

  constructor(
    private fb: FormBuilder,
    private hostRef: ElementRef<HTMLElement>
  ) { }

  ngOnInit(): void {
    this.initSubmissionsList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.checklistAnswers) return;

    // Lista nova vinda do backend = estado salvo. Zera o dirty.
    this.initSubmissionsList();
  }

  initSubmissionsList() {
    this.submissionsList = [];
    this.checklistAnswers.forEach(answer => this.submissionsList.push(new CoverableSubmission(_.cloneDeep(answer))));
    this.dirty = false;
    this.touch();
  }

  /** "Descartar" da barra de confirmação: volta ao último estado salvo. */
  discardChanges() {
    this.initSubmissionsList();
    this.initFormArrayControls(this.submissionsList);
    this.table?.renderRows();
  }

  reloadTable() {
    this.initFormArrayControls(this.submissionsList);
    this.table.renderRows();
    this.touch();
  }

  getSubmissionAnswerField(index: number) : FormGroup {
    return this.formArray.controls[index] as FormGroup;
  }

  /** Control do campo de ID da linha — ligado ao `<input>` nativo da célula. */
  getSubmissionIdControl(index: number): FormControl {
    return this.getSubmissionAnswerField(index).get('submissionId') as FormControl;
  }

  extractSubmissionsIds(rawList: string | FormArray) : string[] {
    if (typeof rawList == 'string') {
      let submissionsIds = rawList.split('\n');

      if (submissionsIds.length == 1) {
        const rawElement = submissionsIds[0];
        if (rawElement.includes('\t')) {
          submissionsIds = rawElement.split('\t');
        } else if (rawElement.includes(',')) {
          submissionsIds = rawElement.split(',');
        } else if (rawElement.includes(';')) {
          submissionsIds = rawElement.split(';');
        } else if (rawElement.includes(' ')) {
          submissionsIds = rawElement.split(' ');
        }
      }

      return submissionsIds.filter(sId => sId != '');
    }

    return rawList.value.filter(control => control.status != CheckListAnswerStatus.DELETING).map(answer => answer.submissionId).filter(a => a);
  }

  handleInput(event: any, index: number) {
    this.dirty = true;
    const fieldsNum = this.formArray.controls.length;
    const submissionId = event.target.value;

    this.submissionsList[index].answer.submissionId = submissionId;

    if (index != fieldsNum - 1) return;
    
    this.submissionsList[fieldsNum - 1].answer.submissionId = event.target.value;
    this.submissionsList.push(new CoverableSubmission(undefined, fieldsNum));

    this.reloadTable();
  }

  handlePaste(event: ClipboardEvent, index: number) {
    this.dirty = true;
    const submissionsIds = this.extractSubmissionsIds(event.clipboardData.getData('text'));

    const middleSubList: CoverableSubmission[] = [];

    submissionsIds.map((sId, index) => {
      const answer = new CheckListAnswer();
      answer.order = index + 1;
      answer.submissionId = sId;
      
      middleSubList.push(new CoverableSubmission(answer));
    });

    const beforeSublist = this.submissionsList.slice(0, index);
    const afterSublist = this.submissionsList.slice(index, this.submissionsList.length - 1);

    let formattedSubmissionsList = beforeSublist.concat(middleSubList);

    if (index != this.submissionsList.length) {
      formattedSubmissionsList = formattedSubmissionsList.concat(afterSublist);
    }

    if (index != this.submissionsList.length - 1) {
      const removedIndex = index + middleSubList.length;
      const removed = formattedSubmissionsList.splice(removedIndex, 1)[0];
      formattedSubmissionsList.splice(index + middleSubList.length, 0, new CoverableSubmission(_.cloneDeep(removed.answer), removedIndex + 1));
    }
    
    formattedSubmissionsList.push(new CoverableSubmission());
    
    this.submissionsList = formattedSubmissionsList;
    this.initFormArrayControls(formattedSubmissionsList);
    this.table.renderRows();
    this.touch(); 
  }

  handleTab(index: number, reverseMode?: boolean) {
    this.unselectAllAnswers();

    const nearestSubmissionIndex = this.getNearestSubmissionIndex(index, reverseMode);

    if (nearestSubmissionIndex == -1) {
      return;
    }

    this.selectAnswer(nearestSubmissionIndex);
  }

  getNearestSubmissionIndex(startingIndex: number, reverseMode?: boolean): number {
    let i = startingIndex + (reverseMode ? -1 : 1);
    let indexFound = false;

    while (i >= 0 && i < this.submissionsList.length) {
      const coverableSubmission = this.submissionsList[i];
      
      if (this.submissionIsSelectable(coverableSubmission)) {
        indexFound = true;
        break;
      }

      reverseMode ? i-- : i++;
    }

    return indexFound ? i : -1;
  }

  getSubmissionAnswerStatus(submission: CoverableSubmission): CheckListAnswerStatus {
    const filter = this.checklistAnswers.filter(answer => answer.submissionId == submission.answer.submissionId && answer.order == submission.answer.order);

    return filter.length == 0 ? CheckListAnswerStatus.UNCATEGORIZED : filter[0].status;
  }

  saveLastSelectedAnswer(index: number) {
    const submissionId = this.submissionsList[index].answer.submissionId;

    if (!submissionId) {
      return;
    }

    this.lastSelectedIndex = index;
    this.lastSelectedSubmissionId = submissionId;
  }

  handleControlA(index: number) {
    this.saveLastSelectedAnswer(index);
    this.selectAllAnswers();
  }

  selectAllAnswers() {
    for (let i = 0; i < this.submissionsList.length; i++) {
      this.selectAnswer(i);
    }
  }

  unselectAllAnswers() {
    this.submissionsList.forEach(s => s.selected = false);
    this.touch();
  }

  uncoverAllSelectedSubmissions() {
    this.submissionsList.filter(s => s.selected && this.submissionIsSelectable(s)).forEach(selectedSubmission => {
      const originalIndex = this.submissionsList.indexOf(selectedSubmission);
      this.uncover(originalIndex);
    });

    this.deleteOccurred = true;
  }

  handleRowClick(event: MouseEvent, index: number) {
    if (!event.ctrlKey && !event.metaKey) {
      this.unselectAllAnswers();
    }

    if (event.shiftKey && this.lastSelectedIndex != undefined) {
      const indexesLimits = [index, this.lastSelectedIndex].sort();
      const firstIndex = indexesLimits[0];
      const lastIndex = indexesLimits[1];

      for (let i = firstIndex; i < lastIndex; i++) {
        this.selectAnswer(i);
      }
    }

    const coverableSubmission = this.submissionsList[index];

    if ((event.ctrlKey || event.metaKey) && coverableSubmission.selected && !event.shiftKey) {
      coverableSubmission.selected = false;
      this.touch();
      return;
    }

    this.selectAnswer(index);
    this.saveLastSelectedAnswer(index);
  }

  selectAnswer(index: number) {

    const indexIsValid = index < this.submissionsList.length;

    if (!indexIsValid) {
      return;
    }

    const coverableSubmission = this.submissionsList[index];

    if (!this.submissionIsSelectable(coverableSubmission)) {
      return;
    }

    coverableSubmission.selected = true;
    this.touch();
  }

  submissionIsSelectable(submission: CoverableSubmission): boolean {
    const answer = submission.answer;

    return !submission.toBeDeleted && answer.submissionId != undefined;
  }

  statusLength(status: CheckListAnswerStatus | string): number {
    return this.statusCounts[status as string] || 0;
  }

  initFormArrayControls(submissionsList: CoverableSubmission[]) {
    this.formArray = this.fb.array(submissionsList.map(submission => this.fb.group({
      id: [submission.answer.id],
      order: [submission.answer.order],
      status: [submission.answer.status],
      submissionId: [{ value: submission.answer.submissionId, disabled: submission.toBeDeleted }]
    })));
  }

  update() {
    this.reloadTable();
    const submissionsId = this.extractSubmissionsIds(this.formArray);
    this.updateList.emit(submissionsId);
  }

  uncover(index: number) {
    const submission = this.submissionsList[index];

    if (!submission.answer.submissionId || submission.toBeDeleted) {
      return;
    }

    setTimeout(() => {
      submission.selected = false;
      this.touch();
    });

    if(submission.answer.status == CheckListAnswerStatus.UNCATEGORIZED) {
      this.submissionsList.splice(index, 1);
      this.dirty = true;
      this.reloadTable();
      return;
    }
    
    submission.toBeDeleted = true;
    submission.answer.status = CheckListAnswerStatus.DELETING;
    this.formArray.controls[index].get('submissionId').disable();
    this.dirty = true;
    this.touch();
  }

  recover(index: number) {
    const submission = this.submissionsList[index];

    if (!submission.toBeDeleted) {
      return;
    }

    setTimeout(() => {
      submission.selected = false;
      this.touch();
    });

    submission.toBeDeleted = false;
    submission.answer.status = this.getSubmissionAnswerStatus(submission);
    this.formArray.controls[index].get('submissionId').enable();
    this.dirty = true;
    this.touch();
  }

  /**
   * Leva o foco à primeira linha com o status pedido (clique num contador da
   * legenda). Acha a LINHA pelo índice no modelo e o campo pelo ViewChildren
   * correspondente — antes descia oito `children[0]` a partir do elemento da
   * linha, o que amarrava a legenda ao aninhamento exato de cada célula.
   */
  focusOnFirstOccurrence(status: CheckListAnswerStatus) {
    const index = this.submissionsList.findIndex(s => s.answer.status == status);

    if (index < 0) {
      return;
    }

    // `#idInput` agora é o próprio `<input>` (era o host do `app-text-input`).
    const input = this.idInputs?.toArray()[index]?.nativeElement;

    input?.focus();
    (input?.closest('tr') ?? input)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  getStatusClass(status: string): string {
    return status.replace('_', '-').toLowerCase();
  }

  /**
   * Glifo (Material Symbols) por status. Acrescenta uma terceira via de leitura
   * ao lado da cor da linha e do rótulo — a cor sozinha não é acessível, e o
   * rótulo sozinho não se destaca numa tabela densa.
   */
  private static readonly STATUS_ICONS: { [statusClass: string]: string } = {
    'ok': 'check_circle',
    'not-found': 'search_off',
    'unrelated': 'link_off',
    'duplicated': 'content_copy',
    'uncategorized': 'help',
    'invalid': 'block',
    'deleting': 'delete'
  };

  getStatusIcon(status: string): string {
    return PublicationFixedControlComponent.STATUS_ICONS[this.getStatusClass(status)] ?? 'help';
  }

}
